import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MusicVisualizer from './MusicVisualizer';
import './MiniAudioPlayer.css';

const FANPAGE_API = process.env.REACT_APP_FANPAGE_API || process.env.REACT_APP_API_URL || 'http://localhost:5010/api';
const DEFAULT_COVER_OPACITY = 0.98;
const ASSET_BASE = (process.env.REACT_APP_FANPAGE_ASSET_BASE || process.env.REACT_APP_FRONTPAGE_ASSET_BASE || FANPAGE_API).replace(/\/api\/?$/i, '').replace(/\/+$/, '');
const SHARED_AUDIO_PLAY_EVENT = 'dodsego:music-audio-play-request';
const PAUSE_MINI_PLAYER_EVENT = 'dodsego:music-audio-pause-mini-player';
const MINI_PLAYER_TOGGLE_EVENT = 'dodsego:music-toggle-mini-player';
const MINI_PLAYER_PLAY_EVENT = 'dodsego:music-play-mini-player';
const MUSIC_STATE_EVENT = 'dodsego:music-state';
const MUSIC_LAST_TRACK_ID_KEY = 'dodsego.musicplayer.lastTrackId.v1';
const MUSIC_LAST_TRACK_TITLE_KEY = 'dodsego.musicplayer.lastTrackTitle.v1';
const MUSIC_LAST_PLAYING_KEY = 'dodsego.musicplayer.isPlaying.v1';

const apiUrl = (path) => {
  if (!path.startsWith('/api')) return path;
  const base = String(FANPAGE_API || '').replace(/\/+$/, '');
  if (!base) return path;
  if (/\/api$/i.test(base)) return `${base}${path.replace(/^\/api/i, '')}`;
  return `${base}${path}`;
};

const toMediaProxyUrl = (url) => {
  if (!url) return '';
  const normalized = normalizeStorageUrl(url);
  if (!/^https:\/\/(storage\.googleapis\.com|storage\.cloud\.google\.com)\//i.test(normalized)) {
    return normalized;
  }
  return apiUrl(`/api/media-proxy?url=${encodeURIComponent(normalized)}`);
};

const resolveSceneAudioUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const normalized = String(url).replace(/^\/api\/uploads/i, '/uploads');
  if (!ASSET_BASE) return normalized;
  return `${ASSET_BASE}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
};

const isHttp = (url) => /^https?:\/\//i.test(url || '');

const normalizeStorageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('gs://')) {
    const path = url.replace(/^gs:\/\//i, '');
    return `https://storage.googleapis.com/${path}`;
  }
  if (/^https:\/\/storage\.cloud\.google\.com\//i.test(url)) {
    return url.replace('https://storage.cloud.google.com/', 'https://storage.googleapis.com/');
  }
  return url;
};

const clampOpacity = (value, fallback = DEFAULT_COVER_OPACITY) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(1, Math.max(0.9, num));
};

const pickAudioUrl = (scene) => {
  const audio = scene?.audio || {};
  const lib = audio.library || {};
  const candidates = [
    audio.url,
    audio.storageUrl,
    audio.originalUrl,
    lib.storageUrl,
    lib.originalUrl,
    ...(Array.isArray(lib.links) ? lib.links : []),
  ];
  const absolute = candidates.find((u) => isHttp(u) || String(u || '').startsWith('gs://'));
  if (absolute) return toMediaProxyUrl(absolute);
  const relative = candidates.find((u) => typeof u === 'string' && u.trim());
  if (relative) return resolveSceneAudioUrl(relative);
  return '';
};

const resolveCoverUrl = (scene) => {
  const lib = scene?.audio?.library || {};
  const candidates = [
    lib.coverThumbUrl,
    lib.coverUrl,
    lib.thumbUrl,
    lib.optimizedUrl,
    lib.storageUrl,
    lib.originalUrl,
  ];
  const absolute = candidates.find((u) => isHttp(u) || String(u || '').startsWith('gs://'));
  if (absolute) return normalizeStorageUrl(absolute);
  const relative = candidates.find((u) => typeof u === 'string' && u.trim());
  if (relative) return resolveSceneAudioUrl(relative);
  return '';
};

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const whole = Math.floor(seconds);
  const mins = Math.floor(whole / 60);
  const secs = String(whole % 60).padStart(2, '0');
  return `${mins}:${secs}`;
};

const getPremiereMs = (scene) => {
  const raw = scene?.audio?.library?.premiereDate;
  if (!raw) return 0;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return 0;
  return d.getTime();
};

const MiniAudioPlayer = ({ inHeader = false, startOpen = false, lockOpen = false, embedded = false, headless = false }) => {
  const audioRef = useRef(null);
  const [audioEl, setAudioEl] = useState(null);
  const canvasRef = useRef(null);
  const decodeCtxRef = useRef(null);
  const waveformDataRef = useRef(null);
  const waveformCacheRef = useRef(new Map());
  const [open, setOpen] = useState(Boolean(startOpen));
  const [visualizerOn] = useState(true);
  const [tracks, setTracks] = useState([]);
  const [currentId, setCurrentId] = useState('');
  const [loadError, setLoadError] = useState('');
  const [playError, setPlayError] = useState('');
  const retryRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [waveformLoading, setWaveformLoading] = useState(false);
  const [panelSize, setPanelSize] = useState({ width: 720, height: 440 });
  const [panelAspectRatio, setPanelAspectRatio] = useState(16 / 9);
  const panelRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [visualizerDim, setVisualizerDim] = useState(DEFAULT_COVER_OPACITY);
  const [infoTab, setInfoTab] = useState('about');
  const [infoPanelExpanded, setInfoPanelExpanded] = useState(false);
  const [isPageHidden, setIsPageHidden] = useState(typeof document !== 'undefined' ? document.hidden : false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );
  const [embeddedEntryPhase, setEmbeddedEntryPhase] = useState('idle');
  const embeddedModePrevRef = useRef(Boolean(embedded && !headless));
    useEffect(() => {
      if (audioRef.current && audioRef.current !== audioEl) {
        setAudioEl(audioRef.current);
      }
    }, [audioEl, open]);

    useEffect(() => {
      // visualizer always on
    }, [open]);
  const resizeRef = useRef({ dragging: false, edge: null, startX: 0, startY: 0, startW: 720, startH: 440 });
  const pendingPlayRef = useRef(false);
  const pendingCanPlayHandlerRef = useRef(null);
  const fallbackPlayTimeoutRef = useRef(null);
  const pendingSceneIdRef = useRef('');
  const focusPlayerRequestRef = useRef(false);
  const focusTimersRef = useRef([]);
  const togglePlayRuntimeRef = useRef(() => {});
  const playIfPossibleRef = useRef(() => false);
  const pendingExternalPlayRef = useRef(false);

  const currentTrack = useMemo(() => tracks.find(t => t.id === currentId) || tracks[0], [currentId, tracks]);
  const coverUrl = useMemo(() => resolveCoverUrl(currentTrack?.scene), [currentTrack]);
  const trackOptions = useMemo(() => tracks.map((track) => {
    const albumLabel = String(track?.scene?.audio?.library?.album || '').trim();
    const rawDate = track?.scene?.audio?.library?.premiereDate || track?.scene?.premiereDate;
    const dateLabel = rawDate
      ? (() => {
        const d = new Date(rawDate);
        if (Number.isNaN(d.getTime())) return '';
        return new Intl.DateTimeFormat('no-NO', { day: '2-digit', month: 'long', year: 'numeric' }).format(d);
      })()
      : '';
    const label = isMobileViewport
      ? track.title
      : (dateLabel ? `${track.title} (${dateLabel})` : track.title);
    return {
      id: track.id,
      label,
      album: albumLabel,
    };
  }), [tracks, isMobileViewport]);
  const groupedTrackOptions = useMemo(() => {
    const collator = new Intl.Collator('no-NO', { sensitivity: 'base', numeric: true });
    const noAlbum = trackOptions
      .filter((track) => !track.album)
      .sort((a, b) => collator.compare(String(a.label || ''), String(b.label || '')));
    const groupedMap = new Map();
    trackOptions.forEach((track) => {
      if (!track.album) return;
      if (!groupedMap.has(track.album)) groupedMap.set(track.album, []);
      groupedMap.get(track.album).push(track);
    });
    const groups = Array.from(groupedMap.entries())
      .sort(([albumA], [albumB]) => collator.compare(albumA, albumB))
      .map(([album, items]) => ({
        album,
        items: items.slice().sort((a, b) => collator.compare(String(a.label || ''), String(b.label || ''))),
      }));
    return {
      noAlbum,
      groups,
    };
  }, [trackOptions]);
  const trackSelectWidthCh = useMemo(
    () => trackOptions.reduce((max, track) => Math.max(max, String(track?.label || '').length), 0),
    [trackOptions]
  );
  const libraryInfo = useMemo(() => currentTrack?.scene?.audio?.library || {}, [currentTrack?.scene?.audio?.library]);
  const aboutDescription = useMemo(() => {
    const candidates = [
      libraryInfo?.beskrivelse,
      libraryInfo?.Beskrivelse,
      libraryInfo?.description,
      libraryInfo?.Description,
    ];
    const value = candidates.find((entry) => typeof entry === 'string' && entry.trim());
    return String(value || '').trim();
  }, [libraryInfo]);
  const infoLinks = useMemo(() => {
    const lib = libraryInfo || {};
    const rawLinks = [];
    if (Array.isArray(lib.links)) rawLinks.push(...lib.links);
    if (Array.isArray(lib.relatedLinks)) rawLinks.push(...lib.relatedLinks);
    [lib.link, lib.url, lib.website, lib.spotifyUrl, lib.youtubeUrl].forEach((entry) => {
      if (entry) rawLinks.push(entry);
    });

    const mapped = rawLinks.map((entry) => {
      if (!entry) return null;
      if (typeof entry === 'string') {
        const href = String(entry).trim();
        if (!href) return null;
        let label = href;
        try {
          const parsed = new URL(href);
          label = `${parsed.hostname}${parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : ''}`;
        } catch {
          // keep raw string label for non-standard URLs
        }
        return { href, label };
      }
      if (typeof entry === 'object') {
        const href = String(entry.href || entry.url || entry.link || '').trim();
        if (!href) return null;
        const label = String(entry.label || entry.title || entry.name || '').trim() || href;
        return { href, label };
      }
      return null;
    }).filter(Boolean);

    const deduped = [];
    const seen = new Set();
    mapped.forEach((item) => {
      const key = String(item.href || '').toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      deduped.push(item);
    });
    return deduped;
  }, [libraryInfo]);
  const creditRows = useMemo(() => {
    const source = Array.isArray(libraryInfo?.deltagere)
      ? libraryInfo.deltagere
      : (Array.isArray(libraryInfo?.credits) ? libraryInfo.credits : []);
    return source.map((entry) => {
      if (!entry) return null;
      if (typeof entry === 'string') {
        const name = String(entry).trim();
        if (!name) return null;
        return { name, role: '-' };
      }
      const name = String(entry.name || entry.navn || entry.member || '').trim();
      const role = String(entry.rolle || entry.role || entry.instrument || '').trim();
      if (!name && !role) return null;
      return {
        name: name || '-',
        role: role || '-',
      };
    }).filter(Boolean);
  }, [libraryInfo]);
  const hasCredits = creditRows.length > 0;
  const hasAbout = Boolean(aboutDescription);
  const hasLinks = infoLinks.length > 0;
  const hasInlineInfo = Boolean(
    hasAbout || hasLinks || hasCredits
  );
  const handleInfoTabSelect = (nextTab) => {
    setInfoTab(nextTab);
    setInfoPanelExpanded(true);
  };
  const inlineInfoNode = hasInlineInfo ? (
    <div className={`mini-audio-inline-info ${infoPanelExpanded ? 'is-expanded' : 'is-collapsed'}`}>
      {infoPanelExpanded ? (
        <button
          type="button"
          className="mini-audio-inline-info-close"
          onClick={() => setInfoPanelExpanded(false)}
          aria-label="Collapse information panel"
        >
          X
        </button>
      ) : null}
      <div
        key={`mini-audio-inline-info-content-${infoTab}`}
        className={`mini-audio-inline-info-content is-${infoTab} ${infoPanelExpanded ? 'is-visible' : 'is-hidden'}`}
      >
        {infoTab === 'about' && hasAbout ? (
          <>
            <div className="mini-audio-info-row mini-audio-info-row--about">{aboutDescription}</div>
          </>
        ) : null}

        {infoTab === 'links' && hasLinks ? (
          <>
            <ul className="mini-audio-info-links">
              {infoLinks.map((item) => (
                <li key={item.href}>
                  <a href={item.href} target="_blank" rel="noreferrer">{item.label}</a>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {infoTab === 'credits' && hasCredits ? (
          <>
            <table className="mini-audio-info-table">
              <tbody>
                {creditRows.map((credit, idx) => (
                  <tr key={`${credit.name}-${credit.role}-${idx}`}>
                    <td>{credit.name}</td>
                    <td>{credit.role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}
      </div>

      <div className="mini-audio-info-tabs" role="tablist" aria-label="Track information sections">
        {hasCredits ? (
          <button
            type="button"
            className={`mini-audio-info-tab ${infoTab === 'credits' ? 'is-active' : ''}`}
            onClick={() => handleInfoTabSelect('credits')}
          >
            Credits
          </button>
        ) : null}
        {hasAbout ? (
          <button
            type="button"
            className={`mini-audio-info-tab ${infoTab === 'about' ? 'is-active' : ''}`}
            onClick={() => handleInfoTabSelect('about')}
          >
            About
          </button>
        ) : null}
        {hasLinks ? (
          <button
            type="button"
            className={`mini-audio-info-tab ${infoTab === 'links' ? 'is-active' : ''}`}
            onClick={() => handleInfoTabSelect('links')}
          >
            Links
          </button>
        ) : null}
      </div>
    </div>
  ) : null;

  useEffect(() => {
    setInfoPanelExpanded(false);
    if (hasAbout) {
      setInfoTab('about');
      return;
    }
    if (hasLinks) {
      setInfoTab('links');
      return;
    }
    if (hasCredits) {
      setInfoTab('credits');
      return;
    }
    setInfoTab('about');
  }, [currentId, hasAbout, hasLinks, hasCredits]);

  useEffect(() => {
    if (embedded && !headless) {
      setOpen(true);
    }
  }, [embedded, headless]);

  useEffect(() => {
    const isEmbeddedMode = Boolean(embedded && !headless);
    const wasEmbeddedMode = embeddedModePrevRef.current;

    if (isEmbeddedMode && !wasEmbeddedMode && playing) {
      setEmbeddedEntryPhase('seed');
    } else if (!isEmbeddedMode && embeddedEntryPhase !== 'idle') {
      setEmbeddedEntryPhase('idle');
    }

    embeddedModePrevRef.current = isEmbeddedMode;
  }, [embedded, headless, playing, embeddedEntryPhase]);

  useEffect(() => {
    if (embeddedEntryPhase !== 'seed') return undefined;
    const rafId = window.requestAnimationFrame(() => {
      setEmbeddedEntryPhase('ramping');
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [embeddedEntryPhase]);

  useEffect(() => {
    if (embeddedEntryPhase !== 'ramping') return undefined;
    const timerId = window.setTimeout(() => {
      setEmbeddedEntryPhase('idle');
    }, 1100);
    return () => window.clearTimeout(timerId);
  }, [embeddedEntryPhase]);

  const showCollapsedPlay = !open && !embedded && !headless;
  const showFullscreenToggle = open && !isFullscreen && !inHeader && !embedded && !headless;
  const showCloseToggle = !lockOpen && !embedded && !headless;
  const hasActions = showCollapsedPlay || showFullscreenToggle || showCloseToggle;

  const actionsNode = hasActions ? (
    <div className={`mini-audio-actions ${isFullscreen ? 'is-fullscreen' : ''}`}>
      {showCollapsedPlay && (
        <button onClick={togglePlay} className="mini-audio-play" type="button" aria-label={playing ? 'Pause' : 'Play'}>
          <span className={`mini-audio-play-icon ${playing ? 'is-pause' : 'is-play'}`} aria-hidden="true">
            {playing ? '❚❚' : '▶'}
          </span>
        </button>
      )}
      {showFullscreenToggle && (
        <button
          onClick={() => {
            const el = panelRef.current;
            if (!el) return;
            if (document.fullscreenElement) {
              document.exitFullscreen?.();
            } else {
              el.requestFullscreen?.();
            }
          }}
          className="mini-audio-btn mini-audio-btn--square"
          type="button"
        >
          {isFullscreen ? 'Exit full' : 'Fullscreen'}
        </button>
      )}
      {showCloseToggle && (
        <button
          onClick={() => {
            if (open && isFullscreen && document.fullscreenElement) {
              document.exitFullscreen?.();
              return;
            }
            setOpen(o => !o);
          }}
          className={`mini-audio-btn ${open ? 'mini-audio-close' : 'mini-audio-expand'}`}
          type="button"
          aria-label={open ? (isFullscreen ? 'Exit fullscreen' : 'Close player') : 'Expand player'}
        >
          {open ? (isFullscreen ? 'Exit Fullscreen' : '✕') : '□'}
        </button>
      )}
    </div>
  ) : null;

  useEffect(() => {
    if (!coverUrl) {
      setPanelAspectRatio(16 / 9);
      return () => {};
    }

    let cancelled = false;
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => {
      if (cancelled) return;
      const width = Number(image.naturalWidth);
      const height = Number(image.naturalHeight);
      if (!Number.isFinite(width) || !Number.isFinite(height) || height <= 0) {
        setPanelAspectRatio(16 / 9);
        return;
      }
      const ratio = width / height;
      setPanelAspectRatio(Number.isFinite(ratio) && ratio > 0 ? ratio : 16 / 9);
    };
    image.onerror = () => {
      if (!cancelled) setPanelAspectRatio(16 / 9);
    };
    image.src = coverUrl;

    return () => {
      cancelled = true;
    };
  }, [coverUrl]);

  useEffect(() => {
    let active = true;
    const loadScenes = async () => {
      try {
        const res = await fetch(apiUrl('/api/visualizer/scenes?fanpage=1'));
        if (!res.ok) throw new Error('Failed to load scenes');
        const data = await res.json();
        const list = Array.isArray(data?.scenes) ? data.scenes : [];
        const fanpageScenes = list.filter(s => s?.fanpageEnabled);
        const baseList = fanpageScenes.length
          ? fanpageScenes
          : list.filter(s => (s?.status || '').toLowerCase() === 'published' || (s?.status || '').toLowerCase() === 'public' || s?.status == null);
        const mapped = baseList
          .map(s => {
            const audio = s?.audio || {};
            const lib = audio.library || {};
            const title = lib.subject || lib.title || lib.name || s.title || 'Untitled';
            const url = normalizeStorageUrl(pickAudioUrl(s));
            return {
              id: String(s.id || s._id || title),
              title,
              sceneTitle: s.title || title,
              file: url,
              scene: s,
            };
          })
          .filter(t => !!t.file)
          .sort((a, b) => getPremiereMs(b.scene) - getPremiereMs(a.scene));
        if (active) {
          if (mapped.length) {
            const storedTrackId = (() => {
              try {
                return String(window.localStorage.getItem(MUSIC_LAST_TRACK_ID_KEY) || '').trim();
              } catch {
                return '';
              }
            })();
            setTracks(mapped);
            setLoadError('');
            setCurrentId((prev) => {
              if (prev && mapped.some((track) => track.id === prev)) return prev;
              if (storedTrackId && mapped.some((track) => track.id === storedTrackId)) return storedTrackId;
              return mapped[0].id;
            });
          } else {
            setTracks([]);
            setCurrentId('');
            setLoadError('No public audio scenes found.');
          }
        }
      } catch {
        if (active) {
          setTracks([]);
          setCurrentId('');
          setLoadError('Unable to load audio scenes.');
        }
      }
    };
    loadScenes();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const isFs = document.fullscreenElement === panelRef.current;
      setIsFullscreen(isFs);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    const onOpen = (event) => {
      focusTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      focusTimersRef.current = [];
      focusPlayerRequestRef.current = true;
      if (!headless) setOpen(true);
      const requestedSceneId = String(event?.detail?.sceneId || '').trim();
      if (!requestedSceneId) return;
      const match = tracks.find((track) => {
        const trackSceneId = String(track?.scene?.id || track?.scene?._id || track?.id || '').trim();
        return trackSceneId === requestedSceneId || String(track?.id || '').trim() === requestedSceneId;
      });
      if (match?.id) {
        setCurrentId(match.id);
        pendingSceneIdRef.current = '';
      } else {
        pendingSceneIdRef.current = requestedSceneId;
      }
    };
    window.addEventListener('mini-audio-open', onOpen);
    return () => window.removeEventListener('mini-audio-open', onOpen);
  }, [headless, tracks]);

  useEffect(() => {
    const onPauseRequest = () => {
      const audio = audioRef.current;
      if (!audio) return;
      if (!audio.paused) {
        audio.pause();
      }
      setPlaying(false);
    };
    window.addEventListener(PAUSE_MINI_PLAYER_EVENT, onPauseRequest);
    return () => window.removeEventListener(PAUSE_MINI_PLAYER_EVENT, onPauseRequest);
  }, []);

  useEffect(() => {
    const onToggleRequest = () => {
      focusPlayerRequestRef.current = true;
      if (!headless) setOpen(true);
      window.setTimeout(() => {
        togglePlayRuntimeRef.current();
      }, 0);
    };
    window.addEventListener(MINI_PLAYER_TOGGLE_EVENT, onToggleRequest);
    return () => window.removeEventListener(MINI_PLAYER_TOGGLE_EVENT, onToggleRequest);
  }, [headless]);

  useEffect(() => {
    const onPlayRequest = () => {
      focusPlayerRequestRef.current = true;
      if (!headless) setOpen(true);
      pendingExternalPlayRef.current = true;

      window.setTimeout(() => {
        const playedNow = playIfPossibleRef.current();
        if (playedNow) {
          pendingExternalPlayRef.current = false;
        }
      }, 0);
    };

    window.addEventListener(MINI_PLAYER_PLAY_EVENT, onPlayRequest);
    return () => window.removeEventListener(MINI_PLAYER_PLAY_EVENT, onPlayRequest);
  }, [headless]);

  useEffect(() => {
    if (!inHeader || !open || !focusPlayerRequestRef.current) return;

    const focusPanelInView = () => {
      const panel = panelRef.current;
      if (!panel) return;
      panel.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      if (typeof panel.focus === 'function') {
        try {
          panel.focus({ preventScroll: true });
        } catch {
          panel.focus();
        }
      }
    };

    const timerIds = [0, 220, 700, 1150].map((delay) => window.setTimeout(focusPanelInView, delay));
    timerIds.push(window.setTimeout(() => {
      focusPlayerRequestRef.current = false;
    }, 1400));
    focusTimersRef.current = timerIds;

    return () => {
      timerIds.forEach((timerId) => window.clearTimeout(timerId));
      focusTimersRef.current = [];
    };
  }, [currentId, inHeader, open]);

  useEffect(() => () => {
    focusTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    focusTimersRef.current = [];
  }, []);

  useEffect(() => {
    const pendingSceneId = String(pendingSceneIdRef.current || '').trim();
    if (!pendingSceneId || !tracks.length) return;
    const match = tracks.find((track) => {
      const trackSceneId = String(track?.scene?.id || track?.scene?._id || track?.id || '').trim();
      return trackSceneId === pendingSceneId || String(track?.id || '').trim() === pendingSceneId;
    });
    if (match?.id) {
      setCurrentId(match.id);
      pendingSceneIdRef.current = '';
    }
  }, [tracks]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const onChange = (event) => setIsMobileViewport(event.matches);
    setIsMobileViewport(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onChange);
      return () => mediaQuery.removeEventListener('change', onChange);
    }
    mediaQuery.addListener(onChange);
    return () => mediaQuery.removeListener(onChange);
  }, []);

  useEffect(() => {
    if (!currentId) return;
    try {
      window.localStorage.setItem(MUSIC_LAST_TRACK_ID_KEY, String(currentId));
    } catch {
      // Ignore storage write errors.
    }
  }, [currentId]);

  useEffect(() => {
    const activeTitle = String(currentTrack?.title || currentTrack?.sceneTitle || '').trim() || 'DODSEGO';

    try {
      window.localStorage.setItem(MUSIC_LAST_TRACK_TITLE_KEY, activeTitle);
      window.localStorage.setItem(MUSIC_LAST_PLAYING_KEY, playing ? '1' : '0');
    } catch {
      // Ignore storage write errors.
    }

    window.dispatchEvent(new CustomEvent(MUSIC_STATE_EVENT, {
      detail: {
        title: activeTitle,
        playing: Boolean(playing),
        trackId: String(currentId || ''),
      },
    }));
  }, [currentId, currentTrack?.sceneTitle, currentTrack?.title, playing]);

  useEffect(() => {
    const onVisibilityChange = () => setIsPageHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    if (!pendingExternalPlayRef.current) return;
    const playedNow = playIfPossibleRef.current();
    if (playedNow) {
      pendingExternalPlayRef.current = false;
    }
  }, [currentId, currentTrack?.file]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack?.file) return;
    pendingPlayRef.current = false;
    if (fallbackPlayTimeoutRef.current) {
      window.clearTimeout(fallbackPlayTimeoutRef.current);
      fallbackPlayTimeoutRef.current = null;
    }
    if (pendingCanPlayHandlerRef.current) {
      audio.removeEventListener('canplay', pendingCanPlayHandlerRef.current);
      pendingCanPlayHandlerRef.current = null;
    }
    if (retryRef.current) {
      window.clearTimeout(retryRef.current);
      retryRef.current = null;
    }
    audio.pause();
    audio.removeAttribute('crossorigin');
    audio.crossOrigin = '';
    audio.preload = 'metadata';
    audio.src = currentTrack.file;
    audio.load();
    setPlayError('');
    setPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
  }, [currentTrack]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoaded = () => {
      setDuration(audio.duration || 0);
      setPlayError('');
      if (retryRef.current) {
        window.clearTimeout(retryRef.current);
        retryRef.current = null;
      }
      if (pendingPlayRef.current) {
        pendingPlayRef.current = false;
        ensureAudioContext();
        audio.play().then(() => setPlaying(true)).catch((err) => {
          setPlayError(err?.message || 'Playback blocked.');
        });
      }
    };
    const onTime = () => {
      const d = audio.duration || 0;
      setProgress(d ? audio.currentTime / d : 0);
      setCurrentTime(audio.currentTime || 0);
    };
    const onEnded = () => {
      setPlaying(false);
      const idx = tracks.findIndex(t => t.id === currentId);
      const next = tracks[(idx + 1) % tracks.length];
      setCurrentId(next.id);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onError = () => {
      setPlayError('Audio failed to load. Check CORS or URL.');
    };

    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('loadeddata', onLoaded);
    audio.addEventListener('canplay', onLoaded);
    audio.addEventListener('canplaythrough', onLoaded);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('loadeddata', onLoaded);
      audio.removeEventListener('canplay', onLoaded);
      audio.removeEventListener('canplaythrough', onLoaded);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('error', onError);
    };
  }, [currentId, tracks]);

  useEffect(() => {
    let cancelled = false;
    const shouldPrepareWaveform = !headless && (open || embedded);
    if (!currentTrack?.file || !shouldPrepareWaveform) {
      waveformDataRef.current = null;
      setWaveformLoading(false);
      return;
    }

    const shouldDeferEmbeddedWaveformLoad = embedded && playing && embeddedEntryPhase !== 'idle';
    if (shouldDeferEmbeddedWaveformLoad) {
      setWaveformLoading(false);
      return;
    }

    const cacheKey = currentTrack.file;
    const cachedWaveform = waveformCacheRef.current.get(cacheKey);
    if (cachedWaveform) {
      waveformDataRef.current = cachedWaveform;
      setWaveformLoading(false);
      return;
    }

    const abortController = new AbortController();

    const loadWaveform = async () => {
      try {
        if (shouldPrepareWaveform) setWaveformLoading(true);
        const res = await fetch(cacheKey, { mode: 'cors', signal: abortController.signal });
        const arrayBuffer = await res.arrayBuffer();
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = decodeCtxRef.current ?? new Ctx();
        decodeCtxRef.current = ctx;

        const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
        if (cancelled) return;

        const channelData = audioBuffer.getChannelData(0);
        const samples = 240;
        const blockSize = Math.max(1, Math.floor(channelData.length / samples));
        const peaks = [];
        for (let i = 0; i < samples; i += 1) {
          const start = i * blockSize;
          const end = Math.min(start + blockSize, channelData.length);
          let max = 0;
          for (let j = start; j < end; j += 1) {
            const v = Math.abs(channelData[j]);
            if (v > max) max = v;
          }
          peaks.push(max);
        }
        const maxVal = Math.max(...peaks, 1);
        const normalizedPeaks = peaks.map(v => v / maxVal);
        waveformCacheRef.current.set(cacheKey, normalizedPeaks);
        waveformDataRef.current = normalizedPeaks;
      } catch (error) {
        if (error?.name !== 'AbortError') {
          waveformDataRef.current = null;
        }
      } finally {
        if (!cancelled) setWaveformLoading(false);
      }
    };

    loadWaveform();
    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [currentTrack, headless, open, embedded, playing, embeddedEntryPhase]);

  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width * dpr));
    const height = Math.max(1, Math.floor(rect.height * dpr));
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;

    ctx.clearRect(0, 0, width, height);
    const data = waveformDataRef.current || [];
    const mid = height / 2;
    const scale = height * 0.45;

    ctx.fillStyle = 'rgba(255, 140, 0, 0.6)';
    if (!data.length) {
      ctx.fillRect(0, mid - 1, width, 2);
      return;
    }
    const stride = width / data.length;
    for (let i = 0; i < data.length; i += 1) {
      const v = data[i] * scale;
      const x = Math.round(i * stride);
      ctx.fillRect(x, mid - v, 1, v * 2);
    }

    ctx.fillStyle = 'rgba(255, 140, 0, 0.95)';
    const progressX = width * (Number.isFinite(progress) ? progress : 0);
    ctx.fillRect(progressX, 0, 2, height);
  }, [progress]);

  useEffect(() => {
    if (isPageHidden) return;
    drawWaveform();
  }, [drawWaveform, waveformLoading, currentTrack, open, embedded, headless, panelSize.width, panelSize.height, isPageHidden]);

  const ensureAudioContext = () => {
    if (!decodeCtxRef.current) return;
    if (decodeCtxRef.current.state === 'suspended') {
      decodeCtxRef.current.resume().catch(() => {});
    }
  };

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio || !currentTrack?.file) return;
    if (audio.paused) {
      window.dispatchEvent(new CustomEvent(SHARED_AUDIO_PLAY_EVENT, {
        detail: { playerId: '__mini_player__' },
      }));
      ensureAudioContext();
      pendingPlayRef.current = true;
      setPlayError('Buffering audio…');
      if (audio.readyState >= 2) {
        audio.play().then(() => { setPlaying(true); setPlayError(''); }).catch((err) => {
          setPlayError(err?.message || 'Playback blocked.');
        });
      } else {
        if (pendingCanPlayHandlerRef.current) {
          audio.removeEventListener('canplay', pendingCanPlayHandlerRef.current);
          pendingCanPlayHandlerRef.current = null;
        }
        const onCanPlay = () => {
          audio.removeEventListener('canplay', onCanPlay);
          pendingCanPlayHandlerRef.current = null;
          audio.play().then(() => { setPlaying(true); setPlayError(''); }).catch((err) => {
            setPlayError(err?.message || 'Playback blocked.');
          });
        };
        pendingCanPlayHandlerRef.current = onCanPlay;
        audio.addEventListener('canplay', onCanPlay, { once: true });
        audio.load();
      }
      // Fallback: try muted playback once if still blocked
      if (fallbackPlayTimeoutRef.current) {
        window.clearTimeout(fallbackPlayTimeoutRef.current);
      }
      fallbackPlayTimeoutRef.current = window.setTimeout(() => {
        fallbackPlayTimeoutRef.current = null;
        if (!audio.paused) return;
        audio.muted = true;
        audio.play().then(() => {
          audio.muted = false;
          setPlaying(true);
          setPlayError('');
        }).catch(() => {});
      }, 1200);
    } else {
      pendingPlayRef.current = false;
      if (fallbackPlayTimeoutRef.current) {
        window.clearTimeout(fallbackPlayTimeoutRef.current);
        fallbackPlayTimeoutRef.current = null;
      }
      if (pendingCanPlayHandlerRef.current) {
        audio.removeEventListener('canplay', pendingCanPlayHandlerRef.current);
        pendingCanPlayHandlerRef.current = null;
      }
      audio.pause();
      setPlaying(false);
      setPlayError('');
    }
  }

  togglePlayRuntimeRef.current = togglePlay;
  playIfPossibleRef.current = () => {
    const audio = audioRef.current;
    if (!audio || !currentTrack?.file) return false;
    if (audio.paused) {
      togglePlayRuntimeRef.current();
    }
    return true;
  };

  const onSeek = (nextProgress) => {
    const audio = audioRef.current;
    if (!audio || duration <= 0) return;
    const p = Math.max(0, Math.min(1, Number(nextProgress)));
    if (!Number.isFinite(p)) return;
    audio.currentTime = p * duration;
    setProgress(p);
  };

  const onWaveformClick = (e) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(0, e.clientX - rect.left), rect.width);
    const p = rect.width ? x / rect.width : 0;
    onSeek(p);
  };

  const onSelectTrack = (e) => {
    setCurrentId(e.target.value);
  };

  const selectNext = useCallback(() => {
    if (!tracks.length) return;
    const idx = tracks.findIndex(t => t.id === currentId);
    const next = tracks[(idx + 1) % tracks.length];
    setCurrentId(next.id);
  }, [tracks, currentId]);

  const selectPrev = useCallback(() => {
    if (!tracks.length) return;
    const idx = tracks.findIndex(t => t.id === currentId);
    const prev = tracks[(idx - 1 + tracks.length) % tracks.length];
    setCurrentId(prev.id);
  }, [tracks, currentId]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const title = currentTrack?.title || currentTrack?.sceneTitle || 'DØDSEGO';
    const artwork = coverUrl
      ? [
        { src: coverUrl, sizes: '96x96', type: 'image/png' },
        { src: coverUrl, sizes: '128x128', type: 'image/png' },
        { src: coverUrl, sizes: '192x192', type: 'image/png' },
        { src: coverUrl, sizes: '256x256', type: 'image/png' },
        { src: coverUrl, sizes: '384x384', type: 'image/png' },
        { src: coverUrl, sizes: '512x512', type: 'image/png' },
      ]
      : [];

    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title,
        artist: 'DØDSEGO',
        album: 'Fanpage Player',
        artwork,
      });
    } catch {}
  }, [currentTrack, coverUrl]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
    } catch {}
  }, [playing]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    if (!Number.isFinite(duration) || duration <= 0) return;
    try {
      navigator.mediaSession.setPositionState({
        duration,
        playbackRate: 1,
        position: Math.max(0, Math.min(currentTime, duration)),
      });
    } catch {}
  }, [currentTime, duration]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;

    const playFromSession = async () => {
      const audio = audioRef.current;
      if (!audio) return;
      try {
        await audio.play();
      } catch {}
    };

    const pauseFromSession = () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.pause();
    };

    const seekRelative = (seconds) => {
      const audio = audioRef.current;
      if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
      const nextTime = Math.max(0, Math.min(audio.duration, (audio.currentTime || 0) + seconds));
      audio.currentTime = nextTime;
      setCurrentTime(nextTime);
      setProgress(audio.duration ? nextTime / audio.duration : 0);
    };

    const seekTo = (details) => {
      const audio = audioRef.current;
      if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
      const nextTime = Math.max(0, Math.min(audio.duration, Number(details?.seekTime || 0)));
      audio.currentTime = nextTime;
      setCurrentTime(nextTime);
      setProgress(audio.duration ? nextTime / audio.duration : 0);
    };

    const setHandler = (name, handler) => {
      try {
        navigator.mediaSession.setActionHandler(name, handler);
      } catch {}
    };

    setHandler('play', playFromSession);
    setHandler('pause', pauseFromSession);
    setHandler('previoustrack', selectPrev);
    setHandler('nexttrack', selectNext);
    setHandler('seekbackward', (details) => seekRelative(-(details?.seekOffset || 10)));
    setHandler('seekforward', (details) => seekRelative(details?.seekOffset || 10));
    setHandler('seekto', seekTo);

    return () => {
      setHandler('play', null);
      setHandler('pause', null);
      setHandler('previoustrack', null);
      setHandler('nexttrack', null);
      setHandler('seekbackward', null);
      setHandler('seekforward', null);
      setHandler('seekto', null);
    };
  }, [selectNext, selectPrev]);

  useEffect(() => {
    const onMove = (ev) => {
      if (!resizeRef.current.dragging) return;
      const dx = ev.clientX - resizeRef.current.startX;
      const dy = ev.clientY - resizeRef.current.startY;
      let nextW = resizeRef.current.startW;
      let nextH = resizeRef.current.startH;
      if (resizeRef.current.edge.includes('right')) nextW = resizeRef.current.startW + dx;
      if (resizeRef.current.edge.includes('left')) nextW = resizeRef.current.startW - dx;
      if (resizeRef.current.edge.includes('bottom')) nextH = resizeRef.current.startH + dy;
      if (resizeRef.current.edge.includes('top')) nextH = resizeRef.current.startH - dy;
      nextW = Math.max(420, Math.min(960, nextW));
      nextH = Math.max(300, Math.min(640, nextH));
      const side = Math.max(nextW, nextH);
      setPanelSize({ width: side, height: side });
    };
    const onUp = () => {
      if (!resizeRef.current.dragging) return;
      resizeRef.current.dragging = false;
      resizeRef.current.edge = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  const startResize = (edge) => (ev) => {
    ev.preventDefault();
    resizeRef.current.dragging = true;
    resizeRef.current.edge = edge;
    resizeRef.current.startX = ev.clientX;
    resizeRef.current.startY = ev.clientY;
    resizeRef.current.startW = panelSize.width;
    resizeRef.current.startH = panelSize.height;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = edge.includes('left') || edge.includes('right') ? 'ew-resize' : 'ns-resize';
  };

  const showEmbeddedSceneBackdrop = embedded && !headless;
  const embeddedSceneMotionTarget = embedded && !headless
    ? (embeddedEntryPhase === 'seed' ? 0.1 : (playing ? 1 : 0.1))
    : 1;
  const embeddedSceneMotionTransitionSeconds = embeddedEntryPhase === 'ramping' && playing ? 1 : 2;
  const embeddedAnalyserDeferMs = embeddedEntryPhase !== 'idle' && playing ? 1000 : 0;

  return (
    <>
    {showEmbeddedSceneBackdrop ? (
      <div
        className={`mini-audio-embedded-scene ${audioEl ? 'is-on' : ''}`}
        aria-hidden="true"
        style={{
          opacity: audioEl ? embeddedSceneMotionTarget : 0,
          transitionDuration: `${embeddedSceneMotionTransitionSeconds}s`,
        }}
      >
        {audioEl && (
          <MusicVisualizer
            audio={audioEl}
            active={visualizerOn && open && !isPageHidden}
            currentTime={currentTime}
            progress={progress}
            scene={currentTrack?.scene}
            targetFps={60}
            motionTarget={embeddedSceneMotionTarget}
            motionTransitionSeconds={embeddedSceneMotionTransitionSeconds}
            deferAnalyserMs={embeddedAnalyserDeferMs}
          />
        )}
        <div
          className="mini-audio-embedded-scene-dim"
          style={{
            opacity: visualizerDim,
            backgroundImage: coverUrl ? `url("${coverUrl}")` : 'none',
          }}
        />
      </div>
    ) : null}

    <div className={`mini-audio-player ${inHeader ? 'mini-audio-player--header' : ''} ${embedded ? 'mini-audio-player--embedded' : ''} ${headless ? 'mini-audio-player--headless' : ''}`}>
      {!headless ? (
      <div
        className={`mini-audio-panel ${open ? 'mini-audio-panel--open' : 'mini-audio-panel--min'} ${isFullscreen ? 'is-fullscreen' : ''}`}
        style={{
          ...(open
            ? (inHeader
              ? { '--mini-audio-aspect-ratio': String(panelAspectRatio) }
              : (embedded
                ? { width: 'min(1180px, 96vw)' }
                : { width: panelSize.width, height: panelSize.width }))
            : {}),
          '--mini-audio-panel-image': coverUrl ? `url("${coverUrl}")` : 'none',
        }}
        ref={panelRef}
        tabIndex={inHeader || embedded ? -1 : undefined}
      >
        {open && !isFullscreen && !inHeader && !embedded && (
          <>
            <span className="mini-audio-resize mini-audio-resize--right" onPointerDown={startResize('right')} />
            <span className="mini-audio-resize mini-audio-resize--bottom" onPointerDown={startResize('bottom')} />
            <span className="mini-audio-resize mini-audio-resize--corner" onPointerDown={startResize('right-bottom')} />
          </>
        )}
        {!embedded ? (
        <div className={`mini-audio-visualizer ${audioEl ? 'is-on' : ''}`}>
          {audioEl && (
            <MusicVisualizer
              audio={audioEl}
              active={visualizerOn && open && !isPageHidden}
              currentTime={currentTime}
              progress={progress}
              scene={currentTrack?.scene}
              targetFps={60}
            />
          )}
          <div
            className="mini-audio-visualizer-dim"
            style={{
              opacity: visualizerDim,
              backgroundImage: coverUrl ? `url("${coverUrl}")` : 'none',
            }}
          />
        </div>
        ) : null}
        <div className="mini-audio-overlay" />
        <div className={`mini-audio-content ${hasInlineInfo ? (infoPanelExpanded ? 'is-inline-info-expanded' : 'is-inline-info-collapsed') : ''}`}>
          <div className="mini-audio-header">
            <div>
              <div className="mini-audio-title-row">
                <div className="mini-audio-title">DØDSEGO MUSIC</div>
              </div>
              {tracks.length > 1 ? (
                <div className="mini-audio-track-row">
                  <select
                    className="mini-audio-track-select"
                    value={currentId}
                    onChange={onSelectTrack}
                    style={{
                      width: `calc(${Math.max(trackSelectWidthCh, 1)}ch + 3rem)`,
                      maxWidth: '100%',
                    }}
                  >
                    {groupedTrackOptions.noAlbum.map((track) => (
                      <option key={track.id} value={track.id}>{track.label}</option>
                    ))}
                    {groupedTrackOptions.groups.map((group) => (
                      <optgroup key={group.album} label={group.album}>
                        {group.items.map((track) => (
                          <option key={track.id} value={track.id}>{track.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="mini-audio-subtitle">{currentTrack?.sceneTitle || currentTrack?.title || 'No audio available'}</div>
              )}

              {loadError && <div className="mini-audio-error">{loadError}</div>}
              {playError && <div className="mini-audio-error">{playError}</div>}

            </div>

            {!(inHeader && open && isMobileViewport) && actionsNode}
          </div>

          {inHeader && open ? (
            <div className={`mini-audio-body ${isFullscreen ? 'is-fullscreen' : ''}`}>
              <div className={`mini-audio-waveform ${playing ? 'is-psychedelic' : ''}`}>
                <span className={`mini-audio-loading ${waveformLoading ? 'is-visible' : ''}`}>Waveform loading…</span>
                <canvas ref={canvasRef} onClick={onWaveformClick} />
              </div>

              {isMobileViewport && !isFullscreen && (
                <div className="mini-audio-time-row">{formatTime(currentTime)} / {formatTime(duration)}</div>
              )}

              {isFullscreen && (
                <button
                  type="button"
                  className="mini-audio-exit-fullscreen"
                  onClick={() => {
                    if (document.fullscreenElement) {
                      document.exitFullscreen?.();
                    }
                  }}
                  aria-label="Exit fullscreen"
                >
                  Exit Fullscreen
                </button>
              )}

              {!isFullscreen && (
                <>
                  <div className={`mini-audio-controls ${infoPanelExpanded ? 'is-info-expanded' : 'is-info-collapsed'}`}>
                    <div className="mini-audio-controls-top">
                      <button onClick={togglePlay} className="mini-audio-play" type="button" aria-label={playing ? 'Pause' : 'Play'}>
                        <span className={`mini-audio-play-icon ${playing ? 'is-pause' : 'is-play'}`} aria-hidden="true">
                          {playing ? '❚❚' : '▶'}
                        </span>
                      </button>
                      <span className="mini-audio-time">{formatTime(currentTime)} / {formatTime(duration)}</span>
                    </div>
                    {open ? inlineInfoNode : null}
                  </div>

                  <div className="mini-audio-footer">
                    <>
                      <div className="mini-audio-dimmer">
                        <input
                          type="range"
                          min={0.9}
                          max={1}
                          step={0.01}
                          value={visualizerDim}
                          onChange={(e) => setVisualizerDim(clampOpacity(Number(e.target.value), DEFAULT_COVER_OPACITY))}
                          aria-label="Visualizer darkness"
                        />
                      </div>
                    </>
                  </div>

                  {open && (
                    <div className="mini-audio-fullscreen-row">
                      <button
                        onClick={() => {
                          const el = panelRef.current;
                          if (!el) return;
                          if (document.fullscreenElement) {
                            document.exitFullscreen?.();
                          } else {
                            el.requestFullscreen?.();
                          }
                        }}
                        className="mini-audio-btn mini-audio-btn--square"
                        type="button"
                      >
                        Fullscreen
                      </button>
                    </div>
                  )}

                  {open && isMobileViewport && actionsNode}
                </>
              )}
            </div>
          ) : (
            <>
              {open && (
                <div className="mini-audio-waveform">
                  <span className={`mini-audio-loading ${waveformLoading ? 'is-visible' : ''}`}>Waveform loading…</span>
                  <canvas ref={canvasRef} onClick={onWaveformClick} />
                </div>
              )}

              {open && isFullscreen && (
                <button
                  type="button"
                  className="mini-audio-exit-fullscreen"
                  onClick={() => {
                    if (document.fullscreenElement) {
                      document.exitFullscreen?.();
                    }
                  }}
                  aria-label="Exit fullscreen"
                >
                  Exit Fullscreen
                </button>
              )}

              <div className={`mini-audio-controls ${infoPanelExpanded ? 'is-info-expanded' : 'is-info-collapsed'}`}>
                <div className="mini-audio-controls-top">
                  <button onClick={togglePlay} className="mini-audio-play" type="button" aria-label={playing ? 'Pause' : 'Play'}>
                    <span className={`mini-audio-play-icon ${playing ? 'is-pause' : 'is-play'}`} aria-hidden="true">
                      {playing ? '❚❚' : '▶'}
                    </span>
                  </button>
                  <span className="mini-audio-time">{formatTime(currentTime)} / {formatTime(duration)}</span>
                </div>
                {open ? inlineInfoNode : null}
              </div>

              <div className="mini-audio-footer">
                {open && (
                  <>
                    {!embedded ? (
                    <div className="mini-audio-dimmer">
                      <input
                        type="range"
                        min={0.9}
                        max={1}
                        step={0.01}
                        value={visualizerDim}
                        onChange={(e) => setVisualizerDim(clampOpacity(Number(e.target.value), DEFAULT_COVER_OPACITY))}
                        aria-label="Visualizer darkness"
                      />
                    </div>
                    ) : null}
                  </>
                )}
              </div>
            </>
          )}

        </div>
      </div>
      ) : null}
      <audio ref={audioRef} preload="metadata" playsInline />
    </div>
    {showEmbeddedSceneBackdrop ? (
      <div className="mini-audio-screen-dimmer">
        <input
          type="range"
          min={0.9}
          max={1}
          step={0.01}
          value={visualizerDim}
          onChange={(e) => setVisualizerDim(clampOpacity(Number(e.target.value), DEFAULT_COVER_OPACITY))}
          aria-label="Background opacity"
        />
      </div>
    ) : null}
    </>
  );
};

export default MiniAudioPlayer;
